import type { BackNavigationLocation, MinimalNextRouter } from 'src/common/components/MobileWebViewHeader/backNavigation/types';
import { ENABLED_LOCALES, PAGE_PARAM, PATHS } from 'src/common/constants';

/** Tries to construct location URL from breadcrumbs or slug, else fallback to nearby search */
export const searchPath = (catLocBreadcrumb?: BackNavigationLocation): string => {
  if (catLocBreadcrumb) {
    if (typeof catLocBreadcrumb === 'string') {
      return `${PATHS.SEARCH}${catLocBreadcrumb}/${PAGE_PARAM}`;
    }
    const slug = catLocBreadcrumb?.slug.split('/'); // e.g. events-large-groups/Miami--FL--United-States
    if (slug.length > 1) {
      return `${PATHS.SEARCH}${slug[1]}/${PAGE_PARAM}`;
    }
  }
  return `${PATHS.SEARCH}${PAGE_PARAM}`; // fallback to nearby search page
};

/**
 * `asPath` is preferred to pathname
 * because pathname includes undesirable directory names, e.g. `boat-rental/[[...slug]]`
 * This fn removes all search params.
 */
export const dlpPath = (router: MinimalNextRouter): string => `${router.asPath?.split('?')[0]}`;

export const homePath = (): string => `${PATHS.HOME}`;

/** Only be used to check referrer. Never use to push, because it excludes locales. */
export const newURLWithoutLocale = (referrerUrl: string): URL => {
  const referrer = new URL(referrerUrl);
  for (const locale of ENABLED_LOCALES) {
    if (referrer.pathname.includes(`/${locale}`)) {
      // this will also indirectly remove locale from href
      referrer.pathname = referrer.pathname.replace(`/${locale}`, '');
      break;
    }
  }
  return referrer;
};

/**
 * router.pathname includes leading slash; excludes trailing slash and query params
 * URL.pathname excludes trailing slash and query params
 */
type UrlOrRouter = URL | MinimalNextRouter;

export const isSearch = (url: UrlOrRouter): boolean => {
  if (url instanceof URL) {
    return url.pathname.startsWith(PATHS.SEARCH) && url.searchParams.has('page');
  }
  return url.pathname.startsWith(PATHS.SEARCH) && Boolean(url.query.page);
};

/**
 * Checks for params added via user interaction (like applying FiltersModal or dragging map)
 * Ignored "system" keys of `page`, `filters`, or 'slug'
 * (slug is not visible in URL, but exists in next/router query object)
 */
export const isSearchWithExtraParams = (url: UrlOrRouter): boolean => {
  if (!isSearch(url)) { return false; }
  /** Added "by us" via DLP links, route props or SearchInputProvider['submit']  */
  const ignoreKeys = ['filters', 'page', 'slug'];
  if (url instanceof URL) {
    // using this method because the more concise url.searchParams.size is not supported in iOS 16
    return Array.from(url.searchParams.keys()).some((paramKey) => !ignoreKeys.includes(paramKey));
  }
  return Object.keys(url.query).some((paramKey) => !ignoreKeys.includes(paramKey));
};

export const isDLP = (url: UrlOrRouter): boolean => {
  if (url instanceof URL) {
    return url.pathname.startsWith(PATHS.SEARCH) && !url.searchParams.has('page');
  }

  return url.pathname.startsWith(PATHS.SEARCH) && !url.query.page;
};

export const isBookNow = (url: UrlOrRouter): boolean => {
  if (url instanceof URL) {
    return url.pathname.endsWith(PATHS.BOOK_NOW);
  }
  return url.pathname === '/trips/[listing_id]/book-now';
};

export const isListing = (url: UrlOrRouter): boolean => {
  if (url instanceof URL) {
    return url.pathname.startsWith(PATHS.LISTING)
      && !isBookNow(url)
      && !url.pathname.includes(PATHS.INQUIRY);
  }
  return url.pathname === '/trips/[listing_id]';
};
export const isLegal = (url: UrlOrRouter): boolean => {
  if (url instanceof URL) {
    return url.pathname.startsWith(PATHS.LEGAL);
  }
  return url.pathname.startsWith(PATHS.LEGAL);
};

export const isInquiry = (referrerUrl: URL): boolean => {
  if (referrerUrl.pathname.startsWith(PATHS.LISTING)
    && referrerUrl.pathname.endsWith(PATHS.INQUIRY)) {
    return true;
  }
  return false;
};

export const isHome = (url: UrlOrRouter): boolean => {
  if (url instanceof URL) {
    return url.pathname === PATHS.HOME;
  }
  return url.pathname === PATHS.HOME;
};

/** Parses listing_id from book-now path.  */
export const listingPathFromBookNowPath = (router: MinimalNextRouter): string | null => {
  if (isBookNow(router)) {
    return `${PATHS.LISTING}${router.query.listing_id}/`;
  }
  return null;
};
